import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { DateRangePicker } from '@/components/filters/DateRangePicker'
import { FilterBar, FilterField } from '@/components/filters/FilterBar'
import { MultiSelect } from '@/components/filters/MultiSelect'
import { OptionSelect, type SelectOption } from '@/components/filters/OptionSelect'
import { Input } from '@/components/ui/input'
import type { ReportFilters } from '@/features/report/use-report-filters'
import { shortName, type ReportDef } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { fetchDistinctValues } from '@/services/cube/report'

/** Evet/hayır filtresinin seçenekleri; boş değer "Tümü". */
export const BOOLEAN_OPTIONS: SelectOption[] = [
  { value: '', label: messages.common.all },
  { value: 'true', label: messages.common.yes },
  { value: 'false', label: messages.common.no },
]

export function DistinctMultiSelect({
  view,
  member,
  label,
  selected,
  onChange,
}: {
  view: string
  member: string
  label: string
  selected: string[]
  onChange: (values: string[]) => void
}) {
  // Seçenek listesi filtrelerden bağımsız, tüm zamanların ayrık değerleri; sık değişmez.
  const options = useQuery({
    queryKey: queryKeys.cube(view, { distinct: member }),
    queryFn: () => fetchDistinctValues(member),
    staleTime: Infinity,
  })
  return (
    <MultiSelect
      label={label}
      options={(options.data ?? []).map((value) => ({ value, label: value }))}
      selected={selected}
      onChange={onChange}
      withApply
    />
  )
}

/**
 * Raporun (ya da keşfet tanımının) filtre çubuğu. `children`: rapor tipine özgü kontroller.
 */
export function ReportFilterBar({
  report,
  filters,
  children,
}: {
  report: ReportDef
  filters: ReportFilters
  children?: ReactNode
}) {
  const { state, update } = filters
  const idPrefix = `report-${report.view}`
  const label = (member: string) => report.fields.get(member)?.label ?? member
  const searchShown = report.layout === 'table' && report.search.length > 0
  // Mobilde ilk denetim açık kalır; sayı yalnız katlananlarda etkin olanlar.
  const active = [
    ...report.filters.map((filter) =>
      filter.type === 'multiSelect'
        ? (state.multi[filter.member]?.length ?? 0) > 0
        : filter.type === 'boolean'
          ? state.bool[filter.member] !== undefined
          : false,
    ),
    ...(searchShown ? [!!state.q] : []),
  ]
  const activeCount = active.slice(1).filter(Boolean).length

  return (
    <FilterBar onReset={filters.reset} activeCount={activeCount}>
      {report.filters.map((filter) => {
        switch (filter.type) {
          case 'dateRange':
            return (
              <DateRangePicker
                key={filter.member}
                idPrefix={idPrefix}
                from={state.dateFrom}
                to={state.dateTo}
                defaultRange={typeof filter.default === 'string' ? filter.default : undefined}
                onChange={({ from, to }) => update({ date_from: from, date_to: to })}
              />
            )
          case 'multiSelect':
            return (
              <DistinctMultiSelect
                key={filter.member}
                view={report.view}
                member={filter.member}
                label={label(filter.member)}
                selected={state.multi[filter.member] ?? []}
                onChange={(values) => update({ [shortName(filter.member)]: values })}
              />
            )
          case 'boolean': {
            const value = state.bool[filter.member]
            return (
              <FilterField key={filter.member} label={label(filter.member)}>
                <OptionSelect
                  className="w-44"
                  aria-label={label(filter.member)}
                  value={value === undefined ? '' : String(value)}
                  options={BOOLEAN_OPTIONS}
                  onValueChange={(next) =>
                    update({ [shortName(filter.member)]: next || undefined })
                  }
                />
              </FilterField>
            )
          }
        }
      })}

      {searchShown && (
        <FilterField label={messages.common.search} htmlFor={`${idPrefix}-q`}>
          <Input
            // `key`: filtreler temizlenince kontrolsüz giriş de boşalsın.
            key={state.q ?? ''}
            id={`${idPrefix}-q`}
            className="w-48"
            placeholder={messages.filters.searchPlaceholder}
            defaultValue={state.q ?? ''}
            onBlur={(event) => update({ q: event.target.value || undefined })}
          />
        </FilterField>
      )}

      {children}
    </FilterBar>
  )
}
